import React from 'react';
import { CardActionsMenu } from '@/shared/components/ui/CardActionsMenu';
import type { CardAction } from '@/shared/components/ui/CardActionsMenu';
import { Switch } from '@/shared/components/ui';
import { Icon } from '@/shared/icons';
import type { WorkflowResponse } from '../types';
import { describeExecutionStats } from '../utils/execution-stats-text';
import { getTriggerTypeLabel } from '../utils/node-labels';
import { WorkflowStatusBadge } from './WorkflowStatusBadge';

/**
 * Linha de tabela de workflow (view "Lista"). Padrão-lista travado:
 * checkbox (1ª col) + colunas multi-linha (nome+descrição) + menu ••• (última col).
 * Arrastável para mover entre pastas (drag `workflow-id`).
 */
export interface WorkflowListItemProps {
  workflow: WorkflowResponse;
  selected?: boolean;
  onToggleSelect?: (id: string) => void;
  onEdit: (id: string) => void;
  onDuplicate: (id: string) => void;
  onDelete: (id: string) => void;
  onActivate: (id: string) => void;
  onPause: (id: string) => void;
  onExport: (id: string) => void;
}

export const WorkflowListItem: React.FC<WorkflowListItemProps> = ({
  workflow,
  selected = false,
  onToggleSelect,
  onEdit,
  onDuplicate,
  onDelete,
  onActivate,
  onPause,
  onExport,
}) => {
  // Vários gatilhos por workflow (23/09/2026): o selo mostra o primeiro e "+N".
  const triggers = workflow.definition?.nodes?.filter((n) => n.type.startsWith('trigger_')) ?? [];
  const triggerType = triggers[0]?.type;
  const extraTriggers = triggers.length - 1;
  const nodeCount = workflow.definition?.nodes?.length || 0;
  const isActive = workflow.status === 'active';
  // U2 / D19: sem os contadores na resposta, as 2 células novas ficam VAZIAS —
  // existir elas precisam, senão a grade desalinha do cabeçalho.
  const executionStats = workflow.executionStats;
  const executionLines = executionStats ? describeExecutionStats(executionStats) : null;

  const handleDragStart = (e: React.DragEvent) => {
    e.dataTransfer.setData('workflow-id', workflow.id);
    e.dataTransfer.effectAllowed = 'move';
  };

  const actions: CardAction[] = [
    { label: 'Editar', icon: 'edit', onClick: () => onEdit(workflow.id) },
    { label: 'Duplicar', icon: 'copy', onClick: () => onDuplicate(workflow.id) },
    { label: 'Exportar', icon: 'download', onClick: () => onExport(workflow.id) },
    {
      label: isActive ? 'Desativar' : 'Ativar',
      icon: isActive ? 'pause' : 'play',
      onClick: () => (isActive ? onPause(workflow.id) : onActivate(workflow.id)),
    },
    { label: 'Excluir', icon: 'trash', onClick: () => onDelete(workflow.id), variant: 'danger' },
  ];

  return (
    <tr
      className="border-b transition-colors cursor-grab active:cursor-grabbing"
      style={{ borderColor: 'var(--theme-card-border)', backgroundColor: selected ? 'var(--theme-primary-background)' : undefined }}
      onMouseEnter={(e) => { if (!selected) e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)'; }}
      onMouseLeave={(e) => { if (!selected) e.currentTarget.style.backgroundColor = 'transparent'; }}
      draggable
      onDragStart={handleDragStart}
      data-testid={`workflow-row-${workflow.id}`}
    >
      {/* Checkbox */}
      <td className="pl-4 pr-1 py-3 w-11">
        <button
          type="button"
          onClick={() => onToggleSelect?.(workflow.id)}
          className="w-[18px] h-[18px] rounded-[5px] border-[1.6px] flex items-center justify-center transition-colors"
          style={{
            backgroundColor: selected ? 'var(--theme-primary)' : 'transparent',
            borderColor: selected ? 'var(--theme-primary)' : 'var(--theme-input-border)',
            color: '#ffffff',
          }}
          aria-label={selected ? 'Desmarcar' : 'Selecionar'}
          data-testid={`workflow-check-${workflow.id}`}
        >
          {selected && <Icon name="check" className="w-3 h-3" />}
        </button>
      </td>

      {/* Workflow: nome + descrição */}
      <td className="px-4 py-3">
        <div className="min-w-0 max-w-[420px]">
          <button
            onClick={() => onEdit(workflow.id)}
            className="font-semibold text-sm truncate block text-left hover:underline"
            style={{ color: 'var(--theme-card-text)' }}
            title={workflow.name}
          >
            {workflow.name}
          </button>
          {workflow.description && (
            <div className="text-xs truncate" style={{ color: 'var(--theme-muted-text)' }} title={workflow.description}>
              {workflow.description}
            </div>
          )}
        </div>
      </td>

      {/* Gatilho */}
      <td className="px-4 py-3">
        {triggerType ? (
          <span
            className="inline-flex items-center gap-1.5 h-[22px] px-2 rounded-md text-xs font-semibold"
            style={{ backgroundColor: 'var(--theme-surface-sunken)', color: 'var(--theme-card-text)' }}
          >
            <Icon name="zap" className="w-3 h-3" />
            {getTriggerTypeLabel(triggerType)}
            {extraTriggers > 0 && (
              <span style={{ color: 'var(--theme-muted-text)' }} data-testid={`workflow-extra-triggers-${workflow.id}`}>
                +{extraTriggers}
              </span>
            )}
          </span>
        ) : (
          <span style={{ color: 'var(--theme-muted-text)' }}>—</span>
        )}
      </td>

      {/* Status — F47: switch inline (Switch shared sm) para ligar/desligar;
          pill só nos status não-toggláveis (rascunho/arquivado). O switch
          CONTINUA existindo para "inactive" mesmo quando pausado
          automaticamente (reativação de 1 clique não é removida) — o selo
          "Pausado automaticamente" (com o motivo no title) aparece AO LADO
          do switch quando `autoPause` existir (2026-08-22). */}
      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
        {isActive || workflow.status === 'inactive' ? (
          <div className="inline-flex items-center gap-2" data-testid={`workflow-status-toggle-${workflow.id}`}>
            <Switch
              size="sm"
              checked={isActive}
              onChange={() => (isActive ? onPause(workflow.id) : onActivate(workflow.id))}
            />
            <span
              className="text-xs font-semibold"
              style={{ color: isActive ? 'var(--theme-success-fg)' : 'var(--theme-muted-text)' }}
            >
              {isActive ? 'Ativo' : 'Pausado'}
            </span>
            {workflow.autoPause && (
              <WorkflowStatusBadge status={workflow.status} autoPause={workflow.autoPause} data-testid={`workflow-status-${workflow.id}`} />
            )}
          </div>
        ) : (
          <WorkflowStatusBadge status={workflow.status} autoPause={workflow.autoPause} data-testid={`workflow-status-${workflow.id}`} />
        )}
      </td>

      {/* Etapas */}
      <td className="px-4 py-3 text-sm" style={{ color: 'var(--theme-muted-text)' }}>
        <span className="inline-flex items-center gap-1">
          <Icon name="activity" className="w-3 h-3" />
          {nodeCount}
        </span>
      </td>

      {/* Última execução (U2 / D19) — "há 3 minutos · ✓ Concluída" ou "Nunca executou" */}
      <td
        className="px-4 py-3 text-sm break-words"
        style={{ color: 'var(--theme-muted-text)' }}
        data-testid={`workflow-row-last-execution-${workflow.id}`}
      >
        {executionLines && executionLines.lastExecutionShort}
      </td>

      {/* Execuções (30 dias) — "128 · 3 falhas"; "—" quando não rodou na janela */}
      <td
        className="px-4 py-3 text-sm break-words"
        style={{ color: 'var(--theme-muted-text)' }}
        data-testid={`workflow-row-execution-counts-${workflow.id}`}
      >
        {executionStats && executionLines && (
          executionLines.failures === null ? '—' : (
            <>
              {executionStats.total}
              {' · '}
              <span
                style={{
                  color: executionLines.hasFailures
                    ? 'var(--theme-error-fg)'
                    : 'var(--theme-muted-text)',
                }}
              >
                {executionLines.failures}
              </span>
            </>
          )
        )}
      </td>

      {/* Atualizado */}
      <td className="px-4 py-3 text-sm" style={{ color: 'var(--theme-muted-text)' }}>
        {new Date(workflow.updatedAt).toLocaleDateString('pt-BR')}
      </td>

      {/* Ações */}
      <td className="px-4 py-3 text-right w-14">
        <CardActionsMenu actions={actions} size="sm" data-testid={`workflow-actions-${workflow.id}`} />
      </td>
    </tr>
  );
};

WorkflowListItem.displayName = 'WorkflowListItem';
